import * as React from 'react';
import {useEffect, ReactElement, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {Routes, Route, Navigate, RouteProps, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet, HelmetProvider} from 'react-helmet-async';

import {IStore} from 'redux/interface';
import {getAuthorizationAction} from 'redux/auth/authAsyncActions';
import {changeLocale} from 'redux/translation/translationAsyncActions';
import {firstAuthorizedRoute, RoutesUrl, routeTranslationKeys, TranslationKeys} from 'utils/constants/routeConstants';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {SystemLanguage} from 'utils/constants/constants';

import Callback from 'pages/callback';
import Chat from 'pages/chat';
import Content from 'pages/content';
import Error from 'pages/error';
import Home from 'pages/home';
import Layout from 'components/layout';
import UserSettings from 'pages/user-settings';
import Logout from 'pages/logout';
import {MobileView} from 'components/mobile-view/MobileView';
import {Bounce, ToastContainer} from 'react-toastify';
import {requestForToken} from 'utils/firebase/firebase';
import {addPushToken} from 'redux/api/push';
import PublicContent from '../public-content';
import {AccessDenied} from '../access-dennied/AccessDenied';

const PrivateRoute: React.FunctionComponent<RouteProps> = (
  {
    children
  }
) => {
  const {auth0User, userProfile} = useSelector((state: IStore) => state.auth);
  const [token, setToken] = useState('');
  const location = useLocation();

  const addToken = async (permission: NotificationPermission) => {
    if (permission === 'granted') {
      const firebaseToken = await requestForToken();

      if (firebaseToken) {
        setToken(firebaseToken);
        if (auth0User) {
          addPushToken(firebaseToken).then(r => console.log(`Push token add result = ${r}`));
        }
      }
    }
  };

  useEffect(() => {
    const getToken = async () => {
      Notification.requestPermission()
        .then((permission) => addToken(permission))
        .catch((error) => {
          if (error instanceof TypeError) {
            Notification.requestPermission((permission) => {
              addToken(permission);
            });
          } else {
            throw error;
          }
        });
    };

    getToken();
  }, []);

  const isUserBlocked = () => {
    return auth0User && userProfile && userProfile.block;
  };

  const isUserAuthorised = () => {
    return auth0User;
  };

  return (
    <>
      {isUserAuthorised() && !isUserBlocked() && <Layout>{children as ReactElement}</Layout>}
      {isUserAuthorised() && isUserBlocked() && <Navigate
        to={{
          pathname: RoutesUrl.AccessDenied
        }}
        replace
        state={{from: location}}
      />}
      {!isUserAuthorised() && <Navigate
        to={{
          pathname: RoutesUrl.Home
        }}
        replace
        state={{from: location}}
      />}
    </>
  );
};

const App: React.FunctionComponent<RouteProps> = () => {
  const {
    translation: {
      locale,
      translations
    },
    auth: {
      userProfile
    }
  } = useSelector((state: IStore) => state);

  const dispatch = useDispatch();
  const {pathname} = useLocation();
  const [isMobileView, setIsMobileView] = useState(false);

  useGetTranslations({
    translationKey: TranslationKeys.Common
  });
  useGetTranslations({
    translationKey: TranslationKeys.Toasts
  });
  useGetTranslations({
    translationKey: routeTranslationKeys[firstAuthorizedRoute]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, [pathname]);

  useEffect(() => {
    if (userProfile && userProfile.language !== SystemLanguage.English) {
      dispatch(changeLocale(userProfile.language));
    }
  }, [userProfile, userProfile?.language]);

  useEffect(() => {
    // if (/Android|webOS|iPhone|iPad|iPod|Mac|Macintosh|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    //   setIsMobileView(true);
    // }
    // if (/Mac|Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints === 0) { // macbooks
    //   setIsMobileView(false);
    // }
    setIsMobileView(false);
    dispatch(getAuthorizationAction());
  }, []);

  return (
    <IntlProvider locale={locale} messages={translations[locale]} onError={(e) => console.log('IntlError', e)}>
      <ToastContainer
        role="alert"
        position="bottom-left"
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        hideProgressBar
        limit={5}
        autoClose={30000}
        transition={Bounce}
      />
      <HelmetProvider>
        <Helmet>
          <html lang={locale}/>
        </Helmet>
      </HelmetProvider>

      {
        isMobileView
          ? <MobileView/>
          : <Routes>
            <Route path={RoutesUrl.Home} element={
              <Layout hideSeparator={false}>
                <Home/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Logout} element={
              <Layout>
                <Logout/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Callback} element={
              <Layout>
                <Callback/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.Chat} element={
              <PrivateRoute>
                <Chat/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.ChatRestart} element={
              <PrivateRoute>
                <Chat/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Settings} element={
              <PrivateRoute>
                <UserSettings/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.Content} element={
              <PrivateRoute>
                <Content/>
              </PrivateRoute>
            }/>
            <Route path={RoutesUrl.PublicContent} element={
              <Layout>
                <PublicContent/>
              </Layout>
            }/>
            <Route path={'*'} element={
              <Layout>
                <Error/>
              </Layout>
            }>
            </Route>
            <Route path={RoutesUrl.AccessDenied} element={
              <Layout>
                <AccessDenied/>
              </Layout>
            }>
            </Route>
          </Routes>
      }
    </IntlProvider>
  );
};

export default App;
